import React, { useCallback, useEffect, useState } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { WalletReadyState } from "@solana/wallet-adapter-base";
import { Wallet, Copy, RefreshCw, Unlink, Loader2, ExternalLink, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/ui/modal";
import { CoinIcon } from "@/components/app/AuramaxChip";
import { api, copyText } from "@/lib/api";
import { cn } from "@/lib/utils";

const b64 = (u8) => btoa(String.fromCharCode(...u8));
const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
const deepLink = (w) => `https://${w === "solflare" ? "solflare.com/ul/v1/browse/" : "phantom.app/ul/browse/"}${encodeURIComponent(window.location.href)}?ref=${encodeURIComponent(window.location.origin)}`;

// ---------- wallet picker ----------
function WalletPicker({ open, onClose, onPick }) {
  const { wallets } = useWallet();
  const visible = wallets.filter((w) => [WalletReadyState.Installed, WalletReadyState.Loadable].includes(w.readyState));
  return (
    <Modal open={open} onClose={onClose} testid="wallet-picker">
      <div className="p-5">
        <h3 className="font-display text-lg font-bold text-white">Connect a Solana wallet</h3>
        <p className="mt-1 text-sm text-am-text2">Read-only. AuraMax never holds your keys or moves funds.</p>
        <div className="mt-4 grid gap-2">
          {visible.map((w) => (
            <button key={w.adapter.name} onClick={() => onPick(w.adapter.name)} data-testid={`wallet-option-${w.adapter.name.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-3 rounded-2xl border border-am-border bg-am-surface2 px-4 py-3 text-left hover:bg-white/5">
              <img src={w.adapter.icon} alt="" className="h-8 w-8 rounded-lg" />
              <span className="flex-1 text-sm font-semibold text-white">{w.adapter.name}</span>
              <span className="text-xs text-am-text3">{w.readyState === WalletReadyState.Installed ? "Detected" : "Open"}</span>
            </button>
          ))}
          {visible.length === 0 && (
            <div data-testid="wallet-none" className="rounded-2xl border border-am-border bg-am-surface2 p-4 text-sm text-am-text2">
              No wallet detected in this browser.
              {isMobile() ? (
                <div className="mt-3 grid gap-2">
                  <a href={deepLink("phantom")} data-testid="wallet-open-phantom" className="flex items-center justify-center gap-2 rounded-full border border-am-border-strong py-2.5 text-sm font-semibold text-white"><ExternalLink className="h-4 w-4" /> Open in Phantom</a>
                  <a href={deepLink("solflare")} data-testid="wallet-open-solflare" className="flex items-center justify-center gap-2 rounded-full border border-am-border-strong py-2.5 text-sm font-semibold text-white"><ExternalLink className="h-4 w-4" /> Open in Solflare</a>
                </div>
              ) : <p className="mt-2 text-xs text-am-text3">Install Phantom or Solflare as a browser extension, then come back.</p>}
            </div>
          )}
        </div>
        <p className="mt-4 text-center text-xs text-am-text3">I don't have a wallet — <a href="https://phantom.app" target="_blank" rel="noreferrer" className="underline">get Phantom</a> (free).</p>
      </div>
    </Modal>
  );
}

// ---------- link flow ----------
function useLinkWallet(onLinked) {
  const { select, connect, disconnect, publicKey, signMessage, signIn, wallet, connected, connecting } = useWallet();
  const [pending, setPending] = useState(null); // wallet name awaiting connect
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const pick = (name) => { setStatus(""); setPending(name); select(name); };
  useEffect(() => {
    if (!pending || wallet?.adapter.name !== pending || connected || connecting) return;
    connect().catch((e) => { setStatus(e?.name === "WalletConnectionError" || /reject|cancel/i.test(e?.message) ? "Connection cancelled — you can try again anytime." : (e?.message || "Couldn't connect.")); setPending(null); });
  }, [pending, wallet, connected, connecting, connect]);

  const verify = useCallback(async () => {
    if (!publicKey) return;
    const address = publicKey.toBase58();
    if (!signMessage && !signIn) { setStatus("This wallet can't sign messages; try Phantom or Solflare."); return; }
    setBusy(true); setStatus("Verify ownership in your wallet…");
    try {
      const c = await api.walletChallenge(address);
      let message = c.message, sig;
      if (signIn) {
        try {
          const out = await signIn({ domain: c.domain, address, statement: c.statement, uri: c.uri, version: "1", chainId: c.chain_id, nonce: c.nonce, issuedAt: c.issued_at, expirationTime: c.expires_at });
          message = new TextDecoder().decode(out.signedMessage); sig = out.signature;
        } catch (e) { if (/reject|cancel|denied/i.test(e?.message)) throw e; sig = null; }
      }
      if (!sig) { if (!signMessage) throw new Error("This wallet can't sign messages; try Phantom or Solflare."); sig = await signMessage(new TextEncoder().encode(message)); }
      const me = await api.walletVerify(address, message, b64(sig), wallet?.adapter.name);
      setStatus(""); toast("Wallet linked"); onLinked(me);
    } catch (e) {
      setStatus(/reject|cancel|denied/i.test(e?.message) ? "Signature cancelled — nothing was linked." : (e.message || "Couldn't verify."));
    } finally { setBusy(false); setPending(null); }
  }, [publicKey, signMessage, signIn, wallet, onLinked]);

  useEffect(() => { if (pending && connected && publicKey && !busy) verify(); }, [pending, connected, publicKey, busy, verify]);
  return { pick, status, setStatus, busy, disconnect, publicKey, walletName: wallet?.adapter.name };
}

// ---------- main widget (replaces the old placeholder) ----------
export function WalletPanel() {
  const [me, setMe] = useState(null);
  const [bal, setBal] = useState(null);
  const [balErr, setBalErr] = useState("");
  const [loadingBal, setLoadingBal] = useState(false);
  const [picker, setPicker] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);

  const loadBal = useCallback(async (refresh = false) => {
    setLoadingBal(true); setBalErr("");
    try { setBal(await api.walletBalances(refresh)); } catch (e) { setBalErr(e.message || "Couldn't reach Solana"); } finally { setLoadingBal(false); }
  }, []);
  const onLinked = useCallback((m) => { setMe(m); setPicker(false); loadBal(true); }, [loadBal]);
  const link = useLinkWallet(onLinked);

  useEffect(() => { api.walletMe().then((m) => { setMe(m); if (m.linked) loadBal(); }).catch(() => setMe({ linked: false })); }, [loadBal]);

  const unlink = async () => { try { await api.walletUnlink(); setMe({ ...me, linked: false, address: null }); setBal(null); setConfirmUnlink(false); link.disconnect().catch(() => {}); toast("Wallet unlinked"); } catch (e) { toast(e.message); } };
  const copy = async () => toast((await copyText(me.address)) ? "Address copied" : me.address);
  const mismatch = me?.linked && link.publicKey && link.publicKey.toBase58() !== me.address;

  if (!me) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-am-text3" /></div>;

  return (
    <div data-testid="wallet-panel">
      {!me.linked ? (
        <>
          <div className="font-display text-4xl font-extrabold text-white sm:text-5xl" data-testid="balance-amount">—</div>
          <div className="mt-1 text-sm font-bold tracking-wide text-[#e5c067]">$AURAMAX</div>
          <p className="mt-2 text-xs text-am-text2" data-testid="wallet-unlinked">No wallet linked</p>
          <button onClick={() => { link.setStatus(""); setPicker(true); }} disabled={link.busy} data-testid="connect-wallet" className="mt-6 flex w-full items-center justify-center gap-2 rounded-full aura-gradient py-3.5 text-sm font-bold text-black disabled:opacity-60">
            {link.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wallet className="h-4 w-4" />} {link.busy ? "Verifying…" : "Connect Wallet"}
          </button>
        </>
      ) : (
        <>
          {me.mint_ok === false ? (
            <div data-testid="mint-missing" className="rounded-2xl border border-aura-gold/30 bg-aura-gold/[0.06] p-3 text-sm text-am-text"><AlertTriangle className="mr-1 inline h-4 w-4 text-aura-gold" /> {me.mint_error || "The $AURAMAX mint couldn't be found on Solana."}</div>
          ) : balErr ? (
            <div data-testid="balance-error" className="text-sm text-am-text2">Couldn't reach Solana — <button onClick={() => loadBal(true)} className="font-semibold text-white underline">Retry</button></div>
          ) : !bal || loadingBal ? (
            <div className="mx-auto h-12 w-40 animate-pulse rounded-xl bg-white/10" data-testid="balance-skeleton" />
          ) : (
            <>
              <div className="font-display text-4xl font-extrabold tabular-nums text-white sm:text-5xl" data-testid="balance-amount" title={`${bal.auramax.raw} base units`}>{Number(bal.auramax.ui).toLocaleString(undefined, { maximumFractionDigits: bal.auramax.decimals })}</div>
              <div className="mt-1 text-sm font-bold tracking-wide text-[#e5c067]">$AURAMAX</div>
              <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-am-border bg-am-surface2 px-3 py-1 text-sm text-white" data-testid="sol-balance" title={`${bal.sol.lamports} lamports`}><span className="text-am-text2">SOL</span> {bal.sol.ui}</div>
            </>
          )}
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-aura-mint/30 bg-aura-mint/10 px-3 py-1 font-semibold text-aura-mint" data-testid="wallet-linked"><ShieldCheck className="h-3.5 w-3.5" /> {me.wallet_name || "Wallet"} · {me.short}</span>
            <button onClick={copy} data-testid="wallet-copy" className="inline-flex items-center gap-1 rounded-full border border-am-border px-3 py-1 text-am-text2 hover:text-white"><Copy className="h-3.5 w-3.5" /> Copy</button>
            <button onClick={() => loadBal(true)} disabled={loadingBal} data-testid="wallet-refresh" className="inline-flex items-center gap-1 rounded-full border border-am-border px-3 py-1 text-am-text2 hover:text-white disabled:opacity-50"><RefreshCw className={cn("h-3.5 w-3.5", loadingBal && "animate-spin")} /> Refresh</button>
          </div>
          {mismatch && <p data-testid="wallet-mismatch" className="mt-3 text-xs text-aura-gold">The wallet connected in your extension ({link.publicKey.toBase58().slice(0, 4)}…) isn't your linked wallet.</p>}
          <div className="mt-4 flex gap-2">
            <button onClick={() => { link.setStatus(""); setPicker(true); }} disabled={link.busy} data-testid="wallet-change" className="flex-1 rounded-full border border-am-border-strong py-2.5 text-sm font-semibold text-white hover:bg-white/5 disabled:opacity-60">{mismatch ? "Re-link this wallet" : "Change wallet"}</button>
            <button onClick={() => setConfirmUnlink(true)} data-testid="wallet-unlink" className="flex items-center justify-center gap-1.5 rounded-full border border-aura-magenta/40 px-4 py-2.5 text-sm font-semibold text-aura-magenta hover:bg-white/5"><Unlink className="h-4 w-4" /> Unlink</button>
          </div>
        </>
      )}
      {link.status && <p data-testid="wallet-status" className="mt-3 text-xs text-am-text2">{link.status}</p>}
      <WalletPicker open={picker} onClose={() => setPicker(false)} onPick={(n) => { setPicker(false); link.pick(n); }} />
      <Modal open={confirmUnlink} onClose={() => setConfirmUnlink(false)} testid="wallet-unlink-confirm">
        <div className="p-5">
          <h3 className="font-display text-lg font-bold text-white">Unlink wallet?</h3>
          <p className="mt-1 text-sm text-am-text2">Your balances will stop showing. You can link a wallet again anytime.</p>
          <div className="mt-5 flex gap-2"><button onClick={() => setConfirmUnlink(false)} className="flex-1 rounded-full border border-am-border-strong py-3 text-sm font-semibold text-white">Cancel</button><button onClick={unlink} data-testid="wallet-unlink-confirm-btn" className="flex-1 rounded-full bg-aura-magenta py-3 text-sm font-bold text-white">Unlink</button></div>
        </div>
      </Modal>
    </div>
  );
}

// Compact own-profile chip: real cached balance or "Connect wallet".
export function OwnAuramaxChip({ onClick }) {
  const [v, setV] = useState(undefined);
  useEffect(() => { api.walletMe().then((m) => (m.linked ? api.walletBalances().then((b) => setV(b.auramax.ui)).catch(() => setV(null)) : setV(null))).catch(() => setV(null)); }, []);
  return (
    <button onClick={onClick} data-testid="auramax-chip" className="inline-flex items-center gap-1.5 rounded-full border border-[#d4a84b]/35 bg-[#d4a84b]/10 px-2.5 py-1 text-xs font-semibold text-[#e5c067] hover:bg-[#d4a84b]/20">
      <CoinIcon size={13} />{v === undefined ? "…" : v === null ? "Connect wallet" : <><span className="tabular-nums">{Number(v).toLocaleString()}</span><span className="opacity-80">$AURAMAX</span></>}
    </button>
  );
}
